import React, { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import Modal from '../../parentPortal/components/Modal';
import Button from '../../parentPortal/components/Button';
import { Skeleton, SkeletonRegion } from '../../../components/ui/Skeleton';
import ErrorBlock from '../../parentPortal/components/ErrorBlock';
import ScoreHistoryChart from './ScoreHistoryChart';
import { useGScoreHistory } from '../hooks/useGScoreHistory';
import { downloadCsv, historyToCsv } from '../utils/exportCsv';

const RANGES = [
  { id: '3m',  label: 'Last 3 months', days: 90 },
  { id: '6m',  label: 'Last 6 months', days: 180 },
  { id: '1y',  label: 'Last year',     days: 365 },
  { id: 'all', label: 'All time',      days: null },
];

export default function ScoreHistoryModal({ open, onClose }) {
  const { data, loading, error, reload } = useGScoreHistory({ enabled: open });
  const [rangeId, setRangeId] = useState('all');

  const entries = useMemo(() => data?.entries || [], [data]);
  const filtered = useMemo(() => {
    const range = RANGES.find((r) => r.id === rangeId);
    if (!range?.days) return entries;
    const cutoff = Date.now() - range.days * 24 * 60 * 60 * 1000;
    return entries.filter((e) => new Date(e.takenAt).getTime() >= cutoff);
  }, [entries, rangeId]);

  // A run with no composite was taken but not scored. Including it would make
  // first/last/delta arithmetic on `null` and print a fabricated "+0" where the
  // truth is "not measured". Only scored runs feed the summary numbers — the
  // same filter the chart applies.
  const scored = useMemo(() => filtered.filter((e) => e?.composite != null), [filtered]);

  const stats = useMemo(() => {
    if (!scored.length) return null;
    const first = scored[0].composite;
    const last = scored[scored.length - 1].composite;
    const delta = last - first;
    const best = scored.reduce((m, e) => (e.composite > m.composite ? e : m), scored[0]);
    return { first, last, delta, best };
  }, [scored]);

  const handleExport = () => {
    if (!entries.length) return;
    const csv = historyToCsv(entries);
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(csv, `g-score-history-${today}.csv`);
  };

  // GET /cognition/pillars/{id}/history is out of scope for V1 — no backend
  // behind it. This modal charts the composite only.
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Assessment history — composite"
      maxWidth={720}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button onClick={handleExport} disabled={!entries.length}>
            <Download size={14} strokeWidth={1.5} />
            Export CSV
          </Button>
        </>
      }
    >
      {loading ? (
        /* Range picker pill row, then the chart area — the shape the modal
           settles into, so it doesn't resize under the cursor on open. */
        <SkeletonRegion label="Loading history">
          <Skeleton height={32} radius="var(--radius-full)" style={{ marginBottom: 'var(--space-4)' }} />
          <Skeleton height={180} style={{ marginBottom: 'var(--space-4)' }} />
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-2) 0' }}>
              <Skeleton width="34%" height={12} />
              <Skeleton width="18%" height={12} />
            </div>
          ))}
        </SkeletonRegion>
      ) : error ? (
        <ErrorBlock message={error} onRetry={reload} />
      ) : (
        <>
          {/* Range picker */}
          <div
            style={{
              display: 'flex',
              gap: 'var(--space-1)',
              padding: 'var(--space-1)',
              background: 'var(--bg-tertiary)',
              borderRadius: 'var(--radius-full)',
              marginBottom: 'var(--space-4)',
            }}
            role="tablist"
            aria-label="Time range"
          >
            {RANGES.map((r) => {
              const active = r.id === rangeId;
              return (
                <button
                  key={r.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setRangeId(r.id)}
                  style={{
                    flex: 1,
                    padding: 'var(--space-2) var(--space-3)',
                    borderRadius: 'var(--radius-full)',
                    border: 'none',
                    background: active ? 'var(--bg-elevated)' : 'transparent',
                    color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
                    fontSize: 'var(--text-numeric-size)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                    transition: 'background var(--transition-fast), color var(--transition-fast)',
                  }}
                >
                  {r.label}
                </button>
              );
            })}
          </div>

          {/* Summary tiles. An unscored range still shows the run count, but
              every score-derived number renders as an em dash, never a zero. */}
          {filtered.length > 0 && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                gap: 'var(--space-2)',
                marginBottom: 'var(--space-4)',
              }}
            >
              <StatTile label="Latest composite" value={stats ? stats.last : '—'} />
              <StatTile
                label="Change"
                value={stats ? `${stats.delta >= 0 ? '+' : ''}${stats.delta}` : '—'}
                accent={
                  !stats ? 'neutral' : stats.delta > 0 ? 'success' : stats.delta < 0 ? 'danger' : 'neutral'
                }
              />
              <StatTile label="Best composite" value={stats ? stats.best.composite : '—'} />
              <StatTile label="Assessments" value={filtered.length} />
            </div>
          )}

          {/* Chart */}
          <div
            style={{
              background: 'var(--bg-tertiary)',
              border: '1px solid var(--border-subtle)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-4) var(--space-3) var(--space-2)',
            }}
          >
            <ScoreHistoryChart entries={filtered} />
          </div>

          <p
            style={{
              margin: 'var(--space-4) 0 0',
              fontSize: 'var(--text-label-size)',
              color: 'var(--text-tertiary)',
              lineHeight: 1.5,
            }}
          >
            Each dot is a completed biweekly assessment. Composite scores come only from the assessment battery — daily training keeps the routine but does not move this line.
          </p>
        </>
      )}
    </Modal>
  );
}

function StatTile({ label, value, accent = 'neutral' }) {
  const color =
    accent === 'success'
      ? 'var(--success)'
      : accent === 'danger'
      ? 'var(--danger)'
      : 'var(--text-primary)';
  return (
    <div
      style={{
        background: 'var(--bg-tertiary)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--space-3)',
      }}
    >
      <div
        style={{
          fontSize: 'var(--text-label-size)',
          fontWeight: 700,
          color: 'var(--text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: 'var(--text-label-tracking)',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 800,
          color,
          marginTop: 'var(--space-1)',
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
    </div>
  );
}
